import { FirebaseApp } from "firebase/app";
import { Functions, getFunctions, httpsCallable } from "firebase/functions";
import {
  MusicUploadError,
  MusicUploadErrorType,
  createMusicUploadErrorFromBackend,
  createMusicUploadErrorFromUpload,
} from "./musicErrors";

/**
 * MusicService - serwis do zarządzania plikami muzycznymi
 *
 * ⚠️ NOWA ARCHITEKTURA: Upload bezpośrednio do Google Cloud Storage przez Signed URL
 * Backend generuje Signed URL, frontend uploaduje plik bezpośrednio do GCS.
 * 
 * Funkcjonalności:
 * - Generowanie Signed URL do uploadu
 * - Upload plików muzycznych bezpośrednio do GCS
 * - Obsługa resumable upload dla dużych plików (>10MB)
 * - Obsługa błędów i retry
 */

export interface GenerateUploadUrlRequest {
  tournamentId: string;
  categoryId: string;
  presentationId: string;
  source: "registration" | "admin";
  file: {
    name: string;
    mimeType: string;
    size: number;
  };
}

export interface GenerateUploadUrlResponse {
  success: boolean;
  uploadUrl?: string; // Signed URL (GCS V4)
  musicId?: string; // ID dokumentu presentationMusic
  storagePath?: string; // Docelowa ścieżka w Storage
  expiresAt?: number; // Timestamp wygaśnięcia URL
  error?: string;
}

export interface UploadMusicResponse {
  success: boolean;
  musicId?: string;
  error?: string;
  errorType?: MusicUploadErrorType;
}

export interface DeleteMusicRequest {
  tournamentId: string;
  categoryId: string;
  presentationId: string;
  musicId: string;
}

export interface DeleteMusicResponse {
  success: boolean;
  error?: string;
}

class MusicService {
  private functions: Functions | null = null;
  private firebaseApp: FirebaseApp | null = null;

  constructor(app?: FirebaseApp) {
    this.firebaseApp = app || null;
  }

  private getFunctions(): Functions {
    if (!this.functions) {
      if (!this.firebaseApp) {
        throw new Error("Firebase app nie został przekazany do MusicService");
      }

      // Ustaw region na us-central1 (zgodnie z konfiguracją backendu)
      this.functions = getFunctions(this.firebaseApp, "us-central1");
    }
    return this.functions;
  }

  /**
   * Upload pliku muzycznego do prezentacji
   * 
   * Flow:
   * 1. Pobierz Signed URL z backendu (generateUploadUrl)
   * 2. Upload pliku bezpośrednio do Google Cloud Storage
   * 3. Trigger processMusicFile automatycznie przetworzy plik
   */
  async uploadMusicFile(
    tournamentId: string,
    categoryId: string,
    presentationId: string,
    file: File
  ): Promise<UploadMusicResponse> {
    try {
      // Walidacja rozszerzenia pliku (frontend)
      // TYMCZASOWO: tylko MP3 (WAV będzie dodane później)
      // const allowedExtensions = [".mp3", ".wav"]; // MP3 i WAV (do przywrócenia w przyszłości)
      const allowedExtensions = [".mp3"]; // Tymczasowo tylko MP3
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      if (!allowedExtensions.includes(fileExtension)) {
        return {
          success: false,
          error: `Nieprawidłowy format pliku. Dozwolone: ${allowedExtensions.join(", ")}`,
        };
      }

      // Walidacja rozmiaru pliku (max 20MB - tymczasowo)
      // 50 MB wyłączone na starcie bo zrezygnowaliśmy tymczasowo z WAV
      // const maxSize = 50 * 1024 * 1024; 
      const maxSize = 20 * 1024 * 1024; // 20MB (tymczasowo)
      if (file.size > maxSize) {
        return {
          success: false,
          error: `Plik jest za duży. Maksymalny rozmiar: 20MB. Aktualny: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
        };
      }

      // Określ MIME type
      const mimeType = fileExtension === ".mp3" ? "audio/mpeg" : "audio/wav";

      // Krok 1: Pobierz Signed URL z backendu
      const generateUploadUrl = httpsCallable<
        GenerateUploadUrlRequest,
        GenerateUploadUrlResponse
      >(this.getFunctions(), "generateUploadUrl");

      console.log("📤 [MusicService] Requesting upload URL from backend...", {
        tournamentId,
        categoryId,
        presentationId,
        fileName: file.name,
        fileSize: file.size,
        mimeType,
      });

      const urlResult = await generateUploadUrl({
        tournamentId,
        categoryId,
        presentationId,
        source: "registration",
        file: {
          name: file.name,
          mimeType,
          size: file.size,
        },
      });

      if (!urlResult.data.success || !urlResult.data.uploadUrl || !urlResult.data.musicId) {
        const musicError = new MusicUploadError(
          MusicUploadErrorType.URL_GENERATION_ERROR,
          urlResult.data.error || "Nie udało się uzyskać URL uploadu",
          undefined,
          { step: "generateUploadUrl", response: urlResult.data }
        );
        
        return {
          success: false,
          error: musicError.message,
          errorType: musicError.type,
        };
      }

      const { uploadUrl, musicId, storagePath, expiresAt } = urlResult.data;

      console.log("✅ [MusicService] Upload URL received:", {
        musicId,
        storagePath,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        useResumable: file.size > 10 * 1024 * 1024,
      });

      // Krok 2: Upload pliku bezpośrednio do GCS
      const useResumable = file.size > 10 * 1024 * 1024; // 10MB

      if (useResumable) {
        // Resumable upload dla dużych plików
        console.log("📤 [MusicService] Using resumable upload for large file...");
        const uploadResult = await this.uploadResumable(uploadUrl, file, mimeType);
        if (!uploadResult.success) {
          return uploadResult;
        }
      } else {
        // Standardowy upload dla małych plików
        console.log("📤 [MusicService] Using standard upload...");
        const uploadResult = await this.uploadStandard(uploadUrl, file, mimeType);
        if (!uploadResult.success) {
          return uploadResult;
        }
      }

      console.log("✅ [MusicService] File uploaded successfully:", {
        musicId,
        storagePath,
      });

      return {
        success: true,
        musicId,
      };
    } catch (error) {
      const musicError = createMusicUploadErrorFromBackend(error, {
        step: "uploadMusicFile",
        tournamentId,
        categoryId,
        presentationId,
      });
      
      console.error("❌ [MusicService] Błąd podczas uploadu muzyki:", {
        type: musicError.type,
        message: musicError.message,
        details: musicError.details,
      });
      
      return {
        success: false,
        error: musicError.message,
        errorType: musicError.type,
      };
    }
  }

  /**
   * Standardowy upload (PUT) dla małych plików
   * 
   * Timeout: 5 minut (zgodnie z MUSIC_CONFIG.STANDARD_UPLOAD_TIMEOUT)
   */
  private async uploadStandard(
    uploadUrl: string,
    file: File,
    mimeType: string
  ): Promise<UploadMusicResponse> {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, 5 * 60 * 1000); // 5 minut

    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mimeType,
        },
        body: file,
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        const musicError = createMusicUploadErrorFromUpload(
          response.status,
          response.statusText,
          errorText,
          { step: "uploadStandard" }
        );
        
        console.error("❌ [MusicService] Standard upload failed:", {
          type: musicError.type,
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        
        return {
          success: false,
          error: musicError.message,
          errorType: musicError.type,
        };
      }

      return { success: true };
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Sprawdź czy to timeout (AbortError)
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError = new MusicUploadError(
          MusicUploadErrorType.NETWORK_ERROR,
          "Upload timeout: Przekroczono limit czasu (5 minut). Spróbuj ponownie.",
          error,
          { step: "uploadStandard", timeout: true }
        );
        
        console.error("❌ [MusicService] Standard upload timeout:", {
          type: timeoutError.type,
          message: timeoutError.message,
        });
        
        return {
          success: false,
          error: timeoutError.message,
          errorType: timeoutError.type,
        };
      }
      
      const musicError = createMusicUploadErrorFromBackend(error, {
        step: "uploadStandard",
      });
      
      console.error("❌ [MusicService] Standard upload error:", {
        type: musicError.type,
        message: musicError.message,
      });
      
      return {
        success: false,
        error: musicError.message,
        errorType: musicError.type,
      };
    }
  }

  /**
   * Resumable upload dla dużych plików (>10MB)
   * 
   * Flow:
   * 1. POST z x-goog-resumable: start → otrzymaj Location URL
   * 2. PUT do Location URL z danymi pliku
   * 
   * Timeout: 15 minut dla każdego kroku (zgodnie z MUSIC_CONFIG.RESUMABLE_UPLOAD_TIMEOUT)
   */
  private async uploadResumable(
    uploadUrl: string,
    file: File,
    mimeType: string
  ): Promise<UploadMusicResponse> {
    const initAbortController = new AbortController();
    const initTimeoutId = setTimeout(() => {
      initAbortController.abort();
    }, 15 * 60 * 1000); // 15 minut

    let uploadTimeoutId: NodeJS.Timeout | undefined;

    try {
      // Krok 1: Inicjalizuj resumable upload
      const initResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": mimeType,
          "x-goog-resumable": "start",
        },
        signal: initAbortController.signal,
      });

      clearTimeout(initTimeoutId);

      if (!initResponse.ok) {
        clearTimeout(initTimeoutId);
        const errorText = await initResponse.text();
        const musicError = createMusicUploadErrorFromUpload(
          initResponse.status,
          initResponse.statusText,
          errorText,
          { step: "uploadResumable_init" }
        );
        
        console.error("❌ [MusicService] Resumable upload init failed:", {
          type: musicError.type,
          status: initResponse.status,
          statusText: initResponse.statusText,
          errorText,
        });
        
        return {
          success: false,
          error: musicError.message,
          errorType: musicError.type,
        };
      }

      // Krok 2: Pobierz URL do uploadu z nagłówka Location
      const uploadLocation = initResponse.headers.get("Location");
      if (!uploadLocation) {
        const musicError = new MusicUploadError(
          MusicUploadErrorType.UPLOAD_ERROR,
          "No upload location received from server",
          undefined,
          { step: "uploadResumable_init" }
        );
        
        console.error("❌ [MusicService] No Location header in resumable upload response");
        return {
          success: false,
          error: musicError.message,
          errorType: musicError.type,
        };
      }

      console.log("📤 [MusicService] Resumable upload initialized, uploading data...");

      // Krok 3: Upload danych (PUT do Location URL) z timeoutem
      const uploadAbortController = new AbortController();
      uploadTimeoutId = setTimeout(() => {
        uploadAbortController.abort();
      }, 15 * 60 * 1000); // 15 minut

      const uploadResponse = await fetch(uploadLocation, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": mimeType,
        },
        signal: uploadAbortController.signal,
      });

      clearTimeout(uploadTimeoutId);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        const musicError = createMusicUploadErrorFromUpload(
          uploadResponse.status,
          uploadResponse.statusText,
          errorText,
          { step: "uploadResumable_upload" }
        );
        
        console.error("❌ [MusicService] Resumable upload failed:", {
          type: musicError.type,
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          errorText,
        });
        
        return {
          success: false,
          error: musicError.message,
          errorType: musicError.type,
        };
      }

      return { success: true };
    } catch (error) {
      clearTimeout(initTimeoutId);
      // uploadTimeoutId może nie istnieć jeśli błąd wystąpił przed utworzeniem
      if (uploadTimeoutId) {
        clearTimeout(uploadTimeoutId);
      }
      
      // Sprawdź czy to timeout (AbortError)
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError = new MusicUploadError(
          MusicUploadErrorType.NETWORK_ERROR,
          "Upload timeout: Przekroczono limit czasu (15 minut). Spróbuj ponownie.",
          error,
          { step: "uploadResumable", timeout: true }
        );
        
        console.error("❌ [MusicService] Resumable upload timeout:", {
          type: timeoutError.type,
          message: timeoutError.message,
        });
        
        return {
          success: false,
          error: timeoutError.message,
          errorType: timeoutError.type,
        };
      }
      
      const musicError = createMusicUploadErrorFromBackend(error, {
        step: "uploadResumable",
      });
      
      console.error("❌ [MusicService] Resumable upload error:", {
        type: musicError.type,
        message: musicError.message,
      });
      
      return {
        success: false,
        error: musicError.message,
        errorType: musicError.type,
      };
    }
  }

  /**
   * Usuwa plik muzyczny dla prezentacji
   * 
   * Usuwa:
   * - Dokument z kolekcji presentationMusic
   * - Pliki z Storage (raw i processed)
   * - Aktualizuje flagę hasMusic w prezentacji na false
   */
  async deleteMusicFile(
    tournamentId: string,
    categoryId: string,
    presentationId: string,
    musicId: string
  ): Promise<DeleteMusicResponse> {
    try {
      const deleteMusic = httpsCallable<DeleteMusicRequest, DeleteMusicResponse>(
        this.getFunctions(),
        "deleteMusic"
      );

      console.log("🗑️ [MusicService] Deleting music file...", {
        tournamentId,
        categoryId,
        presentationId,
        musicId,
      });

      const result = await deleteMusic({
        tournamentId,
        categoryId,
        presentationId,
        musicId,
      });

      if (!result.data.success) {
        return {
          success: false,
          error: result.data.error || "Nie udało się usunąć pliku muzycznego",
        };
      }

      console.log("✅ [MusicService] Music file deleted successfully:", {
        tournamentId,
        categoryId,
        presentationId,
        musicId,
      });

      return {
        success: true,
      };
    } catch (error) {
      console.error("❌ [MusicService] Błąd podczas usuwania muzyki:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Nieznany błąd podczas usuwania",
      };
    }
  }
}

// Singleton instance
let musicServiceInstance: MusicService | null = null;

/**
 * Pobiera instancję MusicService
 */
export function getMusicService(app?: FirebaseApp): MusicService {
  if (!musicServiceInstance) {
    musicServiceInstance = new MusicService(app);
  }
  return musicServiceInstance;
}

// Eksport funkcji pomocniczych
export const uploadMusicFile = async (
  tournamentId: string,
  categoryId: string,
  presentationId: string,
  file: File,
  app?: FirebaseApp
): Promise<UploadMusicResponse> => {
  const service = getMusicService(app);
  return service.uploadMusicFile(tournamentId, categoryId, presentationId, file);
};

export const deleteMusicFile = async (
  tournamentId: string,
  categoryId: string,
  presentationId: string,
  musicId: string,
  app?: FirebaseApp
): Promise<DeleteMusicResponse> => {
  const service = getMusicService(app);
  return service.deleteMusicFile(tournamentId, categoryId, presentationId, musicId);
};
